import React, { useState } from "react";
import { requestPasswordReset, confirmPasswordReset } from "../api";

/**
 * Forgot-password flow. Step 1: enter email → a 6-digit code is sent. Step 2:
 * enter the code + a new password → the user is logged straight in (the confirm
 * endpoint returns a token). Reachable from the login page and from the signup
 * page's "account already exists" prompt.
 */
function ResetPasswordPage({ onGoToLogin, onReset, initialEmail = "" }) {
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [step, setStep] = useState(1); // 1=email, 2=code+new password
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await requestPasswordReset(email.trim());
      setStep(2);
      setNotice(`If an account exists for ${email.trim()}, a 6-digit code is on its way. It expires in 10 minutes.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    setLoading(true);
    try {
      await requestPasswordReset(email.trim());
      setCode("");
      setNotice("A new code has been sent.");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await confirmPasswordReset({ email: email.trim(), code: code.trim(), password });
      onReset(); // token is stored by the api helper; parent marks us logged in
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="dark-scope relative min-h-screen flex items-center justify-center px-4 py-10 overflow-hidden" style={{ background: "var(--ink)" }}>
      <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[rgba(201,162,39,0.07)] blur-3xl" />
      <div className="pointer-events-none absolute -bottom-28 -left-24 w-96 h-96 rounded-full bg-[rgba(62,134,134,0.10)] blur-3xl" />

      <div className="relative z-10 w-full max-w-sm animate-rise">
        <div className="text-center mb-7">
          <div className="inline-flex flex-col items-center">
            <span className="text-6xl font-arabic-display text-dt-gold-bright leading-none mb-2" dir="rtl">مراجعة</span>
            <div className="rule-ornament w-40 text-sm"><span className="text-gold-400">✦</span></div>
            <p className="mt-2 text-xs uppercase tracking-[0.25em] text-dt-gold-dim font-medium">murājaʿa</p>
          </div>
        </div>

        <div className="surface-glass rounded-2xl shadow-soft-lg p-8">
          {step === 1 && (
            <>
              <h2 className="text-2xl font-display font-semibold text-ink-800 mb-1">Reset your password</h2>
              <p className="text-sm text-ink-400 mb-6">Enter your email and we'll send you a code to set a new password.</p>

              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-ink-500 mb-1.5">Email address</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-parchment-300 bg-white/80 text-base text-ink-800 placeholder:text-ink-300 focus:outline-none focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)] transition"
                  />
                </div>

                {error && <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</p>}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 rounded-xl bg-dt-gold-bright text-dt-ink font-semibold text-sm hover:brightness-105 disabled:opacity-50 shadow-soft transition-colors"
                >
                  {loading ? "Sending…" : "Send reset code"}
                </button>
              </form>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="text-2xl font-display font-semibold text-ink-800 mb-1">Choose a new password</h2>
              {notice && <p className="text-sm text-ink-500 bg-gold-50 border border-gold-300 rounded-lg px-3 py-2 my-4">{notice}</p>}

              <form onSubmit={handleConfirm} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-ink-500 mb-1.5">6-digit code</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="000000"
                    className="w-full px-4 py-3 rounded-xl border border-parchment-300 bg-white/80 text-2xl text-center font-mono tracking-[0.4em] text-ink-800 focus:outline-none focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)] transition"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-ink-500 mb-1.5">New password</label>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-parchment-300 bg-white/80 text-base text-ink-800 placeholder:text-ink-300 focus:outline-none focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)] transition"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-ink-500 mb-1.5">Confirm new password</label>
                  <input
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat your new password"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-parchment-300 bg-white/80 text-base text-ink-800 placeholder:text-ink-300 focus:outline-none focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)] transition"
                  />
                </div>

                {error && <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</p>}

                <button
                  type="submit"
                  disabled={loading || code.length !== 6}
                  className="w-full py-2.5 rounded-xl bg-dt-gold-bright text-dt-ink font-semibold text-sm hover:brightness-105 disabled:opacity-50 shadow-soft transition-colors"
                >
                  {loading ? "Saving…" : "Set new password"}
                </button>

                <button
                  type="button"
                  onClick={handleResend}
                  disabled={loading}
                  className="w-full py-2.5 rounded-xl border border-parchment-300 bg-white/70 text-ink-600 font-medium text-sm hover:bg-parchment-200 disabled:opacity-50 transition-colors"
                >
                  Resend code
                </button>
              </form>
            </>
          )}

          <p className="mt-6 text-sm text-ink-400 text-center">
            Remembered it?{" "}
            <button
              onClick={onGoToLogin}
              className="text-ink-600 hover:text-ink-700 font-semibold underline-offset-2 hover:underline"
            >
              Back to log in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
